/**
 * TanStack Query hook for verification status with WebSocket invalidation
 * 
 * Verification status can change from:
 * - User completing verification flow
 * - IDComply webhook (async verification completion)
 * - Admin changing verification status
 * 
 * WebSocket events invalidate the cache for real-time updates.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { websocketClient } from '@/lib/websocket-client';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/storage';

export interface VerificationStatus {
  dataVerificationStatus: 'not_started' | 'in_progress' | 'completed' | 'failed';
  idVerificationStatus: 'not_started' | 'in_progress' | 'completed' | 'failed';
  dataVerificationAttempts: number;
  idVerificationAttempts: number;
  canAttemptDataVerification: boolean;
  canAttemptIdVerification: boolean;
}

const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Fetch user verification status with WebSocket invalidation
 * 
 * This hook:
 * - Caches verification status for 10 minutes
 * - Invalidates cache when verification status changes (via WebSocket)
 * - Handles both IDComply webhook updates and admin changes
 */
export function useVerificationStatusQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Subscribe to WebSocket events for cache invalidation
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    // Connect WebSocket (won't reconnect if already connected)
    websocketClient.connect(token).catch(() => {
      // Connection errors handled by websocket client
    });

    // Invalidate verification cache when profile:updated event received
    // This event is emitted by:
    // - IDComply service when verification completes
    // - Admin service when admin changes verification status
    const handleProfileUpdate = (data: { type: string; status: string }) => {
      if (data.type === 'data' || data.type === 'id') {
        queryClient.invalidateQueries({ queryKey: ['verification', 'status'] });
      }
    };

    const unsubscribe = websocketClient.subscribe('profile:updated', handleProfileUpdate);

    return () => {
      unsubscribe();
    };
  }, [user, queryClient]);

  return useQuery({
    queryKey: ['verification', 'status'],
    queryFn: async (): Promise<VerificationStatus> => {
      const response = await apiClient.get<{
        dataVerificationStatus: string;
        idVerificationStatus: string;
        dataVerificationAttempts: number;
        idVerificationAttempts: number;
        canAttemptDataVerification?: boolean;
        canAttemptIdVerification?: boolean;
      }>('/idcomply/verification-status');

      // Max 3 attempts for data verification, unlimited for ID verification
      const dataAttempts = response?.dataVerificationAttempts || 0;
      const idAttempts = response?.idVerificationAttempts || 0;

      return {
        dataVerificationStatus: (response?.dataVerificationStatus as VerificationStatus['dataVerificationStatus']) || 'not_started',
        idVerificationStatus: (response?.idVerificationStatus as VerificationStatus['idVerificationStatus']) || 'not_started',
        dataVerificationAttempts: dataAttempts,
        idVerificationAttempts: idAttempts,
        canAttemptDataVerification: response?.canAttemptDataVerification ?? (dataAttempts < 3),
        canAttemptIdVerification: response?.canAttemptIdVerification ?? true,
      };
    },
    staleTime: TEN_MINUTES,
    gcTime: TEN_MINUTES * 3,
    enabled: !!user,
  });
}
